import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api/client";
import { useAuth } from "@/providers/auth-provider";
import { isStepUpRequired } from "../api/stepup.api";
import { accountKeys, useAccount } from "../hooks/useAccount";
import { useCancelAccountDeletion } from "../hooks/useAccountDeletion";
import { announceGateLift, clearGateLiftAnnouncement } from "../gate-lift";
import { focusMainWhenRevealed } from "../focus-main";

interface AccountDeletionGateProps {
  children: ReactNode;
}

export function AccountDeletionGate({ children }: AccountDeletionGateProps) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useAccount();
  const cancel = useCancelAccountDeletion();
  const [sessionEnded, setSessionEnded] = useState(false);
  const [liftMessage, setLiftMessage] = useState<string | null>(null);
  const [seenFrozen, setSeenFrozen] = useState(false);
  const messageId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const regionRef = useRef<HTMLParagraphElement>(null);

  const scheduledFor = data?.account.deletionScheduledFor ?? null;
  const frozen = isAuthenticated && scheduledFor !== null;

  if (!isAuthenticated) {
    if (seenFrozen) setSeenFrozen(false);
    if (liftMessage !== null) setLiftMessage(null);
  } else if (frozen && !seenFrozen) {
    setSeenFrozen(true);
    if (liftMessage !== null) setLiftMessage(null);
  } else if (!frozen && seenFrozen) {
    setSeenFrozen(false);
    setLiftMessage(t("account.deletion.restored"));
  }

  useEffect(() => {
    if (frozen) {
      clearGateLiftAnnouncement(regionRef.current);
      headingRef.current?.focus();
    }
  }, [frozen]);

  useEffect(() => {
    if (liftMessage === null) return;
    announceGateLift(liftMessage, regionRef.current);
    return focusMainWhenRevealed(liftMessage);
  }, [liftMessage]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const refresh = () => {
      if (document.visibilityState === "visible") {
        void queryClient.invalidateQueries({ queryKey: accountKeys.me() });
      }
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("pageshow", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, [isAuthenticated, queryClient]);

  const handleLogout = () => {
    logout();
    navigate({ to: "/login" });
  };

  const signInAgain = () => {
    logout();
    navigate({ to: "/login", search: { redirect: "/account" } });
  };

  const handleCancel = () => {
    if (cancel.isPending) return;
    cancel.mutate(undefined, {
      onError: (err) => {
        if (err instanceof ApiError && err.status === 401 && !isStepUpRequired(err)) {
          setSessionEnded(true);
        }
      },
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  const cancelMessage = sessionEnded
    ? t("account.deletion.cancelSessionEnded")
    : cancel.error
      ? ((cancel.error instanceof ApiError ? cancel.error.error : undefined) ?? t("common.error"))
      : null;

  const dateLabel =
    scheduledFor !== null
      ? new Date(scheduledFor).toLocaleDateString(i18n.language, { timeZone: "UTC" })
      : "";

  return (
    <>
      {frozen ? (
        <div className="min-h-screen overflow-y-auto bg-void px-4 py-10">
          <div className="mx-auto max-w-xl text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-danger/10">
              <Trash2 size={28} className="text-danger" />
            </div>
            <h1
              ref={headingRef}
              tabIndex={-1}
              className="mt-4 text-2xl font-bold text-heading outline-none"
            >
              {t("account.deletion.gateTitle")}
            </h1>
            <p className="mt-2 text-sm text-muted">
              {t("account.deletion.gateBody", { date: dateLabel })}
            </p>
            <p className="mt-2 text-sm text-muted">{t("account.deletion.gateCredentials")}</p>

            {cancelMessage && (
              <p id={messageId} role="alert" className="mt-4 text-sm text-danger">
                {cancelMessage}
              </p>
            )}

            <div className="mt-8 flex flex-col items-center gap-4">
              <button
                type="button"
                onClick={sessionEnded ? signInAgain : handleCancel}
                aria-disabled={cancel.isPending || undefined}
                aria-busy={cancel.isPending || undefined}
                aria-describedby={sessionEnded ? messageId : undefined}
                className="rounded-lg bg-indigo px-6 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
              >
                {sessionEnded
                  ? t("account.staleSession.action")
                  : cancel.isPending
                    ? t("account.deletion.cancelling")
                    : t("account.deletion.cancel")}
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted transition-colors hover:text-heading"
              >
                <LogOut size={12} />
                {t("nav.logout")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        children
      )}
      <p ref={regionRef} role="status" aria-live="polite" aria-atomic="true" className="sr-only" />
    </>
  );
}
